"use client";

import type { MotionValue } from "framer-motion";
import type { ReactNode } from "react";
import type { HeroDepth } from "@/components/landing/Hero/useHeroMotion";
import FloatCardIconSvg from "@/components/landing/Hero/svg/FloatCardIconSvg";
import { HERO_DEPTH, useParallaxAxis } from "@/components/landing/Hero/useHeroMotion";
import { FloatingCard } from "@/components/landing/shared";

const FLOAT_COPY = {
	en: {
		order: { title: "New order #1042", subtitle: "2m ago" },
		sales: { title: "Total sales", subtitle: "+18%" },
		shipping: { title: "Free shipping over BHD 20", subtitle: undefined },
		payments: { title: "Jana Payments", subtitle: "Paid" },
		live: { title: "Store is live", subtitle: "yourstore.jana.now" },
	},
	ar: {
		order: { title: "طلب جديد #1042", subtitle: "قبل دقيقتين" },
		sales: { title: "إجمالي المبيعات", subtitle: "+18٪" },
		shipping: { title: "شحن مجاني فوق 20 د.ب", subtitle: undefined },
		payments: { title: "مدفوعات جنى", subtitle: "مدفوع" },
		live: { title: "المتجر مباشر", subtitle: "yourstore.jana.now" },
	},
};

function DepthCard({
	depth,
	mx,
	my,
	...rest
}: {
	depth: HeroDepth;
	mx: MotionValue<number>;
	my: MotionValue<number>;
	className: string;
	rotate?: number;
	icon: ReactNode;
	title: string;
	subtitle?: string;
}) {
	const { z, range } = HERO_DEPTH[depth];
	const x = useParallaxAxis(mx, range);
	const y = useParallaxAxis(my, range);
	return <FloatingCard {...rest} x={x} y={y} z={z} />;
}

interface FloatingCardsProps {
	locale: string;
	showExtras: boolean;
	mx: MotionValue<number>;
	my: MotionValue<number>;
}

/**
 * Notification-style cards that float around the browser window. The
 * order/sales pair is always shown; the shipping/payments/live set only
 * appears when the hero is rendering the placeholder mockup (a real product
 * screenshot already tells that story itself).
 */
function FloatingCards(prop: FloatingCardsProps) {
	const { locale, showExtras, mx, my } = prop;
	const f = locale === "ar" ? FLOAT_COPY.ar : FLOAT_COPY.en;

	return (
		<>
			<DepthCard
				className="start-3 top-10 lg:-start-4 lg:top-12 xl:-start-8 xl:top-14"
				rotate={-2}
				depth="front"
				mx={mx}
				my={my}
				icon={<FloatCardIconSvg kind="order" className="h-4 w-4" />}
				title={f.order.title}
				subtitle={f.order.subtitle}
			/>
			<DepthCard
				className="start-3 top-32 lg:-start-6 lg:top-36 xl:-start-10 xl:top-40"
				rotate={2}
				depth="back"
				mx={mx}
				my={my}
				icon={<FloatCardIconSvg kind="sales" className="h-4 w-4" />}
				title={f.sales.title}
				subtitle={f.sales.subtitle}
			/>

			{showExtras && (
				<>
					<DepthCard
						className="end-3 top-4 lg:-end-4 lg:top-6 xl:-end-8 xl:top-8"
						rotate={2}
						depth="front"
						mx={mx}
						my={my}
						icon={<FloatCardIconSvg kind="shipping" className="h-4 w-4" />}
						title={f.shipping.title}
						subtitle={f.shipping.subtitle}
					/>
					<DepthCard
						className="end-3 top-32 lg:-end-6 lg:top-36 xl:-end-10 xl:top-40"
						rotate={-2}
						depth="back"
						mx={mx}
						my={my}
						icon={<FloatCardIconSvg kind="payments" className="h-4 w-4" />}
						title={f.payments.title}
						subtitle={f.payments.subtitle}
					/>
					<DepthCard
						className="end-3 top-56 lg:-end-3 lg:top-60 xl:-end-6 xl:top-64"
						rotate={1}
						depth="front"
						mx={mx}
						my={my}
						icon={
							<span className="relative flex h-2.5 w-2.5">
								<span
									className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
									style={{ background: "var(--success)" }}
								/>
								<span
									className="relative inline-flex h-2.5 w-2.5 rounded-full"
									style={{ background: "var(--success)" }}
								/>
							</span>
						}
						title={f.live.title}
						subtitle={f.live.subtitle}
					/>
				</>
			)}
		</>
	);
}

export default FloatingCards;
